/**
 * TanStack Query Configuration
 *
 * Centralized query client setup with error handling and authentication integration
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { APIError } from '../../../../shared/utils/api-helpers';
import { clearAllAuthState } from '@/services/auth/token-manager';

// ============================================================================
// Error Handling
// ============================================================================

function handleQueryError(error: unknown): void {
  // Suppress expected 404 errors for newsletter images
  if (error instanceof APIError && error.status === 404) {
    // Newsletter images return 404 when not yet generated - this is expected
    // Match various error message formats for missing images
    if (error.message?.includes('No image') ||
        error.message?.includes('not generated') ||
        error.message?.includes('not available')) {
      return; // Silently handle - frontend shows placeholder icon
    }
  }

  console.error('Query error:', error);

  // Handle authentication errors
  if (error instanceof APIError) {
    if (error.status === 401) {
      // Token expired or invalid - clear all auth state and redirect to sign in
      clearAllAuthState();
      window.location.href = window.location.origin;
      return;
    }

    if (error.status === 403) {
      console.warn('Access forbidden:', error.message);
      return;
    }

    if (error.status >= 500) {
      console.error('Server error:', error.message);
      return;
    }
  }
}

function handleMutationError(error: unknown): void {
  console.error('Mutation error:', error);

  // Handle authentication errors for mutations
  if (error instanceof APIError && error.status === 401) {
    clearAllAuthState();
    window.location.href = window.location.origin;
  }
}

// ============================================================================
// Query Client Configuration
// ============================================================================

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),

  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),

  defaultOptions: {
    queries: {
      // Stale time - how long data is considered fresh
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Cache time - how long data stays in cache after component unmounts
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)

      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        if (error instanceof APIError && [401, 403].includes(error.status)) {
          return false;
        }

        // Don't retry on client errors (4xx)
        if (error instanceof APIError && error.status >= 400 && error.status < 500) {
          return false;
        }

        // Retry up to 3 times for server errors
        return failureCount < 3;
      },

      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus (disabled for admin dashboard)
      refetchOnWindowFocus: false,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Background refetch interval (disabled by default)
      refetchInterval: false,
    },

    mutations: {
      // Retry mutations once on network errors
      retry: (failureCount, error) => {
        if (error instanceof APIError && error.status >= 400) {
          return false; // Don't retry client or server errors
        }
        return failureCount < 1; // Retry once for network errors
      },

      // Mutation retry delay
      retryDelay: 1000,
    },
  },
});

// ============================================================================
// Query Keys Factory
// ============================================================================

export const queryKeys = {
  // Authentication
  auth: ['auth'] as const,
  user: () => [...queryKeys.auth, 'user'] as const,

  // Articles
  articles: ['articles'] as const,
  articleList: (params?: Record<string, any>) =>
    [...queryKeys.articles, 'list', params] as const,
  article: (id: number) =>
    [...queryKeys.articles, 'detail', id] as const,

  // Content (unified API)
  content: ['content'] as const,
  contentList: (params?: Record<string, any>) =>
    [...queryKeys.content, 'list', params] as const,
  contentItem: (contentId: string) =>
    [...queryKeys.content, 'detail', contentId] as const,

  // Publishers
  publishers: () => ['publishers'] as const,

  // Newsletters
  newsletters: ['newsletters'] as const,
  newsletterList: (params?: Record<string, any>) =>
    [...queryKeys.newsletters, 'list', params] as const,
  newsletter: (id: number) =>
    [...queryKeys.newsletters, 'detail', id] as const,
  newsletterStats: () => [...queryKeys.newsletters, 'stats'] as const,
  newsletterHealth: () => [...queryKeys.newsletters, 'health'] as const,
  newsletterSearch: (params?: Record<string, any>) =>
    [...queryKeys.newsletters, 'search', params] as const,
  newsletterAnalytics: (period: string) =>
    [...queryKeys.newsletters, 'analytics', period] as const,

  // System
  system: ['system'] as const,
  systemStatus: () => [...queryKeys.system, 'status'] as const,
  healthCheck: () => [...queryKeys.system, 'health'] as const,
  scheduledTasks: () => [...queryKeys.system, 'scheduledTasks'] as const,
  scheduledTaskHistory: (taskName: string) =>
    [...queryKeys.system, 'scheduledTaskHistory', taskName] as const,
  kgQualityIndex: () => [...queryKeys.system, 'kgQualityIndex'] as const,

  // Admin
  admin: ['admin'] as const,
  adminTasks: () => [...queryKeys.admin, 'tasks'] as const,
  adminTask: (taskId: string) =>
    [...queryKeys.admin, 'task', taskId] as const,
  taskQueue: () => [...queryKeys.admin, 'taskQueue'] as const,
  batchLane: () => [...queryKeys.admin, 'batchLane'] as const,

  // Channel Intelligence
  channelIntelligence: ['channelIntelligence'] as const,
  channelEligibility: (publisherId: number) =>
    [...queryKeys.channelIntelligence, 'eligibility', publisherId] as const,
  batchChannelEligibility: (publisherIds: number[]) =>
    [...queryKeys.channelIntelligence, 'eligibility', 'batch', publisherIds.sort().join(',')] as const,
  channelAnalysisProgress: (taskId: string) =>
    [...queryKeys.channelIntelligence, 'progress', taskId] as const,
  publisherAnalysisProgress: (publisherId: number) =>
    [...queryKeys.channelIntelligence, 'publisherProgress', publisherId] as const,
  channelReport: (reportId: string) =>
    [...queryKeys.channelIntelligence, 'report', reportId] as const,
  publisherReports: (publisherId: number) =>
    [...queryKeys.channelIntelligence, 'publisherReports', publisherId] as const,
  latestReport: (publisherId: number) =>
    [...queryKeys.channelIntelligence, 'latestReport', publisherId] as const,
  compareCandidates: (search: string) =>
    [...queryKeys.channelIntelligence, 'compareCandidates', search] as const,
  channelCards: () =>
    [...queryKeys.channelIntelligence, 'channelCards'] as const,
  rollupCandidates: () =>
    [...queryKeys.channelIntelligence, 'rollupCandidates'] as const,
  rollups: (organizationId: string) =>
    [...queryKeys.channelIntelligence, 'rollups', organizationId] as const,
  rollupDetail: (normalizedName: string) =>
    [...queryKeys.channelIntelligence, 'rollupDetail', normalizedName] as const,
  rollupProgress: (taskId: string) =>
    [...queryKeys.channelIntelligence, 'rollupProgress', taskId] as const,
  creatorLinks: (publisherIds: number[]) =>
    [...queryKeys.channelIntelligence, 'creatorLinks', ...publisherIds] as const,
  extendPreview: (publisherId: number, months: number) =>
    [...queryKeys.channelIntelligence, 'extendPreview', publisherId, months] as const,
  extendProgress: (taskId: string) =>
    [...queryKeys.channelIntelligence, 'extendProgress', taskId] as const,

  // Domain & Channel Comparison
  domains: ['domains'] as const,
  domainsList: () =>
    [...queryKeys.domains, 'list'] as const,
  domainDetail: (domainId: number) =>
    [...queryKeys.domains, 'detail', domainId] as const,
  channelComparison: (publisherIds: number[]) =>
    [...queryKeys.domains, 'comparison', publisherIds.sort().join(',')] as const,

  // Pattern Detection
  patterns: ['patterns'] as const,
  patternList: (params?: Record<string, unknown>) =>
    [...queryKeys.patterns, 'list', params] as const,
  patternCandidates: (params?: Record<string, unknown>) =>
    [...queryKeys.patterns, 'candidates', params] as const,
  pattern: (patternId: string) =>
    [...queryKeys.patterns, 'detail', patternId] as const,
  patternTemporal: (patternId: string) =>
    [...queryKeys.patterns, 'temporal', patternId] as const,
  patternTriggers: (patternId: string) =>
    [...queryKeys.patterns, 'triggers', patternId] as const,
  patternPotentialSuccessors: (patternId: string) =>
    [...queryKeys.patterns, 'potential-successors', patternId] as const,
  patternConfidenceDistribution: () =>
    [...queryKeys.patterns, 'confidence-distribution'] as const,
  patternLifecycleDistribution: () =>
    [...queryKeys.patterns, 'lifecycle-distribution'] as const,
  emergingEntities: () =>
    [...queryKeys.patterns, 'emerging-entities'] as const,
  patternImplications: (patternId: string) =>
    [...queryKeys.patterns, 'implications', patternId] as const,
  // CUR-464 — admin-only merge candidates surfaced by the daily scanner
  patternMergeCandidates: () =>
    [...queryKeys.patterns, 'merge-candidates'] as const,

  // Input Sources (Multi-Platform)
  inputSources: ['inputSources'] as const,
  inputPlatforms: () =>
    [...queryKeys.inputSources, 'platforms'] as const,
  inputSourceList: (params?: Record<string, unknown>) =>
    [...queryKeys.inputSources, 'list', params] as const,
  inputSource: (sourceId: string) =>
    [...queryKeys.inputSources, 'detail', sourceId] as const,
  inputSourceSubscribers: (sourceId: string) =>
    [...queryKeys.inputSources, 'subscribers', sourceId] as const,

  // Source Groups
  sourceGroups: ['sourceGroups'] as const,
  sourceGroupList: () => [...queryKeys.sourceGroups, 'list'] as const,
  sourceGroupDetail: (groupId: string) => [...queryKeys.sourceGroups, 'detail', groupId] as const,
  sourceGroupsForSource: (sourceId: string) => [...queryKeys.sourceGroups, 'for-source', sourceId] as const,
  adminOrganizations: () => ['admin', 'organizations'] as const,

  // Creators (Cross-Platform)
  creators: ['creators'] as const,
  creatorList: (params?: Record<string, unknown>) =>
    [...queryKeys.creators, 'list', params] as const,
  creator: (creatorName: string) =>
    [...queryKeys.creators, 'detail', creatorName] as const,

  // Creator Portfolio (Sprint 4c — UUID-keyed)
  creatorSentinelSummary: (creatorId: string) =>
    [...queryKeys.creators, 'sentinel-summary', creatorId] as const,
  creatorAgencyNotes: (creatorId: string) =>
    [...queryKeys.creators, 'agency-notes', creatorId] as const,
  creatorEditorialAdvisory: (creatorId: string, projectId: string) =>
    [...queryKeys.creators, 'editorial-advisory', creatorId, projectId] as const,
  projectShortlists: (projectId: string) =>
    ['shortlists', 'list', projectId] as const,
  projectRoster: (projectId: string) =>
    ['projects', 'roster', projectId] as const,
  projectAgencyNotes: (projectId: string, limit?: number) =>
    ['projects', 'agency-notes', projectId, limit] as const,

  // AI Costs
  costs: ['costs'] as const,
  costsSummary: () => [...queryKeys.costs, 'summary'] as const,
  costsDaily: (days: number) => [...queryKeys.costs, 'daily', days] as const,
  costsByModel: (days: number) => [...queryKeys.costs, 'by-model', days] as const,
  costsByContent: (days: number) => [...queryKeys.costs, 'by-content', days] as const,
  costsItem: (contentId: string) => [...queryKeys.costs, 'item', contentId] as const,
  costsBySource: (days: number, group: string) => [...queryKeys.costs, 'by-source', days, group] as const,
  costsWaste: (days: number) => [...queryKeys.costs, 'waste', days] as const,
  costsEnvelope: (days: number) => [...queryKeys.costs, 'envelope', days] as const,

  // AI Quality (Observability Phase 2 PR-E)
  quality: ['quality'] as const,
  qualitySteps: (days: number) => [...queryKeys.quality, 'steps', days] as const,
  qualityTrend: (days: number, score: string) =>
    [...queryKeys.quality, 'trend', days, score] as const,
  qualityRecent: (days: number, score: string | undefined, below: number, limit: number) =>
    [...queryKeys.quality, 'recent', days, score ?? 'all', below, limit] as const,

  // MCP Usage
  mcpUsage: ['mcpUsage'] as const,
  mcpUsageSummary: (days: number) => [...queryKeys.mcpUsage, 'summary', days] as const,
  mcpUsageDaily: (days: number) => [...queryKeys.mcpUsage, 'daily', days] as const,
  mcpUsageByTool: (days: number) => [...queryKeys.mcpUsage, 'by-tool', days] as const,

  // Admin Projects (cross-org, super-admin only — CUR-530)
  adminProjects: ['admin', 'projects'] as const,
  adminProjectsList: (params?: Record<string, unknown>) =>
    [...queryKeys.adminProjects, 'list', params] as const,
  adminProject: (projectId: string) =>
    [...queryKeys.adminProjects, 'detail', projectId] as const,


  // Admin Inbound Email (CUR-1052 PR-4)
  adminInbound: ['admin', 'inbound'] as const,
  adminInboundAddresses: (params?: Record<string, unknown>) =>
    [...queryKeys.adminInbound, 'addresses', params] as const,
  adminInboundLog: (params?: Record<string, unknown>) =>
    [...queryKeys.adminInbound, 'log', params] as const,

  // Output Channels (Multi-Platform Publishing)
  outputChannels: ['outputChannels'] as const,
  outputPlatforms: () =>
    [...queryKeys.outputChannels, 'platforms'] as const,
  outputChannelList: (params?: Record<string, unknown>) =>
    [...queryKeys.outputChannels, 'list', params] as const,
  outputChannel: (channelId: string) =>
    [...queryKeys.outputChannels, 'detail', channelId] as const,
} as const;

// ============================================================================
// Scoped Query Keys (unified hooks — admin/portal auto-routing)
// ============================================================================

/**
 * Query keys for unified hooks that serve both admin and portal views.
 *
 * Portal-scoped keys start with 'portal', so the existing org-switch
 * invalidation (`queryClient.invalidateQueries({ queryKey: ['portal'] })`)
 * flushes all portal data automatically.
 */
export const scopedKeys = {
  // Pattern hooks
  patterns: (scope: 'admin' | 'portal', params?: Record<string, unknown>) =>
    [scope, 'patterns', 'list', params] as const,
  pattern: (scope: 'admin' | 'portal', id: string) =>
    [scope, 'patterns', 'detail', id] as const,
  patternTemporal: (scope: 'admin' | 'portal', id: string) =>
    [scope, 'patterns', 'temporal', id] as const,
  patternTriggers: (scope: 'admin' | 'portal', id: string) =>
    [scope, 'patterns', 'triggers', id] as const,
  patternSuccessors: (scope: 'admin' | 'portal', id: string) =>
    [scope, 'patterns', 'successors', id] as const,
  patternCandidates: (scope: 'admin' | 'portal', params?: Record<string, unknown>) =>
    [scope, 'patterns', 'candidates', params] as const,

  // Content hooks
  contentList: (scope: 'admin' | 'portal', params?: Record<string, unknown>) =>
    [scope, 'content', 'list', params] as const,
  contentDetail: (scope: 'admin' | 'portal', id: string) =>
    [scope, 'content', 'detail', id] as const,
} as const;

// ============================================================================
// Query Invalidation Helpers
// ============================================================================

export const queryInvalidation = {
  // Invalidate all queries
  all: () => queryClient.invalidateQueries(),

  // Authentication
  auth: () => queryClient.invalidateQueries({ queryKey: queryKeys.auth }),

  // Articles
  articles: () => queryClient.invalidateQueries({ queryKey: queryKeys.articles }),
  articleList: () => queryClient.invalidateQueries({
    queryKey: queryKeys.articles,
    predicate: (query) => query.queryKey[1] === 'list'
  }),

  // Newsletters
  newsletters: () => queryClient.invalidateQueries({ queryKey: queryKeys.newsletters }),
  newsletterList: () => queryClient.invalidateQueries({
    queryKey: queryKeys.newsletters,
    predicate: (query) => query.queryKey[1] === 'list'
  }),
  newsletterStats: () => queryClient.invalidateQueries({
    queryKey: queryKeys.newsletters,
    predicate: (query) => query.queryKey[1] === 'stats'
  }),

  // System
  system: () => queryClient.invalidateQueries({ queryKey: queryKeys.system }),

  // Admin
  admin: () => queryClient.invalidateQueries({ queryKey: queryKeys.admin }),

  // Channel Intelligence
  channelIntelligence: () => queryClient.invalidateQueries({ queryKey: queryKeys.channelIntelligence }),
  channelEligibility: (publisherId: number) => queryClient.invalidateQueries({
    queryKey: queryKeys.channelEligibility(publisherId),
  }),
  batchChannelEligibility: () => queryClient.invalidateQueries({
    queryKey: [...queryKeys.channelIntelligence, 'eligibility', 'batch'],
  }),
  publisherAnalysisProgress: (publisherId: number) => queryClient.invalidateQueries({
    queryKey: queryKeys.publisherAnalysisProgress(publisherId),
  }),
  publisherReports: (publisherId: number) => queryClient.invalidateQueries({
    queryKey: queryKeys.publisherReports(publisherId),
  }),
  latestReport: (publisherId: number) => queryClient.invalidateQueries({
    queryKey: queryKeys.latestReport(publisherId),
  }),
  channelCards: () => queryClient.invalidateQueries({
    queryKey: queryKeys.channelCards(),
  }),

  // Domain & Channel Comparison
  domains: () => queryClient.invalidateQueries({ queryKey: queryKeys.domains }),
  domainsList: () => queryClient.invalidateQueries({
    queryKey: queryKeys.domainsList(),
  }),
  domainDetail: (domainId: number) => queryClient.invalidateQueries({
    queryKey: queryKeys.domainDetail(domainId),
  }),
  channelComparison: (publisherIds: number[]) => queryClient.invalidateQueries({
    queryKey: queryKeys.channelComparison(publisherIds),
  }),

  // Pattern Detection (invalidates both legacy and scoped keys)
  patterns: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.patterns });
    queryClient.invalidateQueries({ queryKey: ['admin', 'patterns'] });
    queryClient.invalidateQueries({ queryKey: ['portal', 'patterns'] });
  },
  patternList: () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.patterns,
      predicate: (query) => query.queryKey[1] === 'list',
    });
    queryClient.invalidateQueries({ queryKey: ['admin', 'patterns', 'list'] });
    queryClient.invalidateQueries({ queryKey: ['portal', 'patterns', 'list'] });
  },
  patternCandidates: () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.patterns,
      predicate: (query) => query.queryKey[1] === 'candidates',
    });
    queryClient.invalidateQueries({ queryKey: ['admin', 'patterns', 'candidates'] });
    queryClient.invalidateQueries({ queryKey: ['portal', 'patterns', 'candidates'] });
  },
  pattern: (patternId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.pattern(patternId) });
    queryClient.invalidateQueries({ queryKey: scopedKeys.pattern('admin', patternId) });
    queryClient.invalidateQueries({ queryKey: scopedKeys.pattern('portal', patternId) });
  },
  patternTemporal: (patternId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.patternTemporal(patternId) });
    queryClient.invalidateQueries({ queryKey: scopedKeys.patternTemporal('admin', patternId) });
    queryClient.invalidateQueries({ queryKey: scopedKeys.patternTemporal('portal', patternId) });
  },
  patternTriggers: (patternId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.patternTriggers(patternId) });
    queryClient.invalidateQueries({ queryKey: scopedKeys.patternTriggers('admin', patternId) });
    queryClient.invalidateQueries({ queryKey: scopedKeys.patternTriggers('portal', patternId) });
  },

  // Input Sources (Multi-Platform)
  inputSources: () => queryClient.invalidateQueries({ queryKey: queryKeys.inputSources }),
  inputSourceList: () => queryClient.invalidateQueries({
    queryKey: queryKeys.inputSources,
    predicate: (query) => query.queryKey[1] === 'list',
  }),
  inputSource: (sourceId: string) => queryClient.invalidateQueries({
    queryKey: queryKeys.inputSource(sourceId),
  }),

  // AI Costs
  costs: () => queryClient.invalidateQueries({ queryKey: queryKeys.costs }),

  // Output Channels (Multi-Platform Publishing)
  outputChannels: () => queryClient.invalidateQueries({ queryKey: queryKeys.outputChannels }),
  outputChannelList: () => queryClient.invalidateQueries({
    queryKey: queryKeys.outputChannels,
    predicate: (query) => query.queryKey[1] === 'list',
  }),
  outputChannel: (channelId: string) => queryClient.invalidateQueries({
    queryKey: queryKeys.outputChannel(channelId),
  }),
};

// ============================================================================
// Prefetch Helpers
// ============================================================================

export const queryPrefetch = {
  systemStatus: () => queryClient.prefetchQuery({
    queryKey: queryKeys.systemStatus(),
    queryFn: async () => {
      const { apiClient } = await import('@/services/api/client');
      return apiClient.getSystemStatus();
    },
    staleTime: 30 * 1000, // 30 seconds
  }),

  healthCheck: () => queryClient.prefetchQuery({
    queryKey: queryKeys.healthCheck(),
    queryFn: async () => {
      const { apiClient } = await import('@/services/api/client');
      return apiClient.getHealthCheck();
    },
    staleTime: 60 * 1000, // 1 minute
  }),
};

// ============================================================================
// Development Tools
// ============================================================================

if (import.meta.env.DEV) {
  // Log query cache changes in development
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'added') {
      console.log('Query added:', event.query.queryKey);
    }
    if (event.type === 'removed') {
      console.log('Query removed:', event.query.queryKey);
    }
  });
}
